import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { noop } from 'lodash-es';
import { getTodaysDate } from '@frontend/date';
import { createShallowStore, createStore } from '@frontend/store';
import { SelectedFiltersType } from '../types';
import { DATE_DISPLAY_FORMAT, getCalendarStartAndEndDateTime } from '../utils/date-helpers';

type CalendarViewV3HeaderFilterStore = {
  filteredCalendarEvents: CalendarEvent[];
  setFilteredCalendarEvents: (filteredCalendarEvents: CalendarEvent[]) => void;

  selectedFilters: SelectedFiltersType;
  setSelectedFilters: (selectedFilters: SelectedFiltersType) => void;

  hasActiveFilters: boolean;
  setHasActiveFilters: (hasActiveFilters: boolean) => void;

  selectedCalendarDate: string;
  calendarStartDateTime: string; // this is computed from selectedCalendarDate & baseTimezone
  calendarEndDateTime: string; // this is computed from selectedCalendarDate & baseTimezone
  setSelectedCalendarDate: (selectedCalendarDate: string, baseTimezone?: string) => void;

  selectedFilteredLocationIds: string[];
  setSelectedFilteredLocationIds: (selectedFilteredLocationIds: string[]) => void;

  refetchCalendarEvents?: () => void;
  setRefetchCalendarEvents?: (refetchCalendarEvents: () => void) => void;

  baseTimezone: string;
  setBaseTimezone: (baseTimezone: string, shouldResetStore?: boolean) => void;
};

const useCalendarViewV3HeaderFilterStore = createStore<CalendarViewV3HeaderFilterStore>(
  (set, get) => ({
    filteredCalendarEvents: [],
    hasActiveFilters: false,
    selectedFilters: {} as SelectedFiltersType,
    selectedCalendarDate: getTodaysDate(DATE_DISPLAY_FORMAT),
    calendarStartDateTime: '',
    calendarEndDateTime: '',
    selectedFilteredLocationIds: [],
    baseTimezone: '',
    refetchCalendarEvents: noop,
    setSelectedFilters: (selectedFilters) => set({ selectedFilters }),
    setHasActiveFilters: (hasActiveFilters) => set({ hasActiveFilters }),
    setFilteredCalendarEvents: (filteredCalendarEvents) => set({ filteredCalendarEvents }),
    setSelectedCalendarDate: (selectedCalendarDate) => {
      const state = get();

      const { startDateTime, endDateTime } = getCalendarStartAndEndDateTime(selectedCalendarDate, state.baseTimezone);

      set({
        selectedCalendarDate,
        calendarStartDateTime: startDateTime,
        calendarEndDateTime: endDateTime,
      });
    },
    setSelectedFilteredLocationIds: (selectedFilteredLocationIds) => set({ selectedFilteredLocationIds }),
    setRefetchCalendarEvents: (refetchCalendarEvents) => set({ refetchCalendarEvents }),
    setBaseTimezone: (baseTimezone, shouldResetStore) => {
      const state = get();
      const { startDateTime, endDateTime } = getCalendarStartAndEndDateTime(state.selectedCalendarDate, baseTimezone);

      set({
        baseTimezone,
        calendarStartDateTime: startDateTime,
        calendarEndDateTime: endDateTime,
        ...(shouldResetStore && {
          filteredCalendarEvents: [],
          hasActiveFilters: false,
          selectedFilters: {} as SelectedFiltersType,
          selectedFilteredLocationIds: [],
          refetchCalendarEvents: noop,
        }),
      });
    },
  }),
  {
    name: 'CalendarViewV3HeaderFilterStore',
  }
);

export const useCalendarViewV3HeaderFilterShallowStore = createShallowStore<CalendarViewV3HeaderFilterStore>(
  useCalendarViewV3HeaderFilterStore
);
